import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from "../../../components/Icon";
import { toast } from 'react-toastify';

import PanelTemplate from '../../../templates/Panel';
import Box from '../../../components/Box';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import InputRadio from '../../../components/InputRadio';
import Modal from '../../../components/Modal';
import { Loading } from '../../../components/Loading';
import api from '../../../services/api';
import { logout } from '../../../services/auth';
import { renderError } from '../../../helpers/errors';
import { WHATSAPP_DOMAIN_CHANGE_URL } from '../../../helpers/constants';
import './styles.scss';

const SITE_IP = process.env.REACT_APP_BRINDEME_SITE_IP;
const SISTEMA_HOST = process.env.REACT_APP_BRINDEME_SISTEMA_HOST;
const BASE_DOMAIN   = process.env.REACT_APP_BRINDEME_DOMAIN;
const SITE_SUFFIX   = `.site.${BASE_DOMAIN}`;
const SYSTEM_SUFFIX = `.sistema.${BASE_DOMAIN}`;

const SITE_DNS   = { type: 'A',     label: 'IP de destino', target: SITE_IP };
const SYSTEM_DNS = { type: 'CNAME', label: 'Apontar para',  target: SISTEMA_HOST, originPlaceholder: 'sistema.seu-domínio.com.br' };

const MANUAL_URL = 'https://ajuda.brinde.me/p/configuracao-de-dominio-3NCua6';

const MODE_DEFAULT = 'default';
const MODE_OWN     = 'own';

const REDIRECT_DELAY_SECONDS = 5;
const LEGACY_SYSTEM_CARD_CLASS = 'domain-card--legacy-system';

const DOMAIN_MODE_OPTIONS = [
    { value: MODE_DEFAULT, label: 'Usar nosso subdomínio' },
    { value: MODE_OWN,     label: 'Usar domínio próprio'  },
];

const DOMAIN_STATUS_VARIANTS = {
    ok:      { color: '#10b981', bg: '#d1fae5' },
    error:   { color: '#6b7280', bg: '#f3f4f6' },
    pending: { color: '#f59e0b', bg: '#fffbec' },
};

const SSL_STATUS = {
    connected:           { variant: 'ok',      text: 'SSL instalado'                },
    disconnected:        { variant: 'error',   text: 'SSL não instalado'            },
    connecting:          { variant: 'pending', text: 'Instalando SSL'               },
    partially_connected: { variant: 'pending', text: 'SSL parcialmente configurado' },
};

const DNS_STATUS = {
    connected:           { variant: 'ok',      text: 'Apontamento identificado'     },
    disconnected:        { variant: 'error',   text: 'Apontamento não identificado' },
    connecting:          { variant: 'pending', text: 'Verificando apontamento'      },
    partially_connected: { variant: 'pending', text: 'Apontamento parcial'          },
};

const CHECKING_STATUS = { variant: 'pending', text: 'Verificando...' };


const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripDomainPrefix = (value, prefix) => {
    if (!prefix) return value;
    return (value ?? '').replace(new RegExp(`^${escapeRegExp(prefix)}`, 'i'), '').replace(/^\.+/, '');
};

const buildDomainWithPrefix = (value, prefix) => {
    if (!prefix || !value) return value;
    return `${prefix}${stripDomainPrefix(value, prefix)}`;
};

const normalizeDomain = value => (value ?? '').replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/\.+$/, '');

const hasDomainFormat = value => normalizeDomain(value).includes('.');

const isDomainEndingWithSuffix = (value, suffix) => normalizeDomain(value).toLowerCase().endsWith((suffix ?? '').toLowerCase());

const getPrefixFromSubdomain = (value, suffix) => {
    const domain = normalizeDomain(value);
    return isDomainEndingWithSuffix(domain, suffix) ? domain.slice(0, -suffix.length) : domain;
};

const buildRedirectUrl = (mode, value, suffix) => {
    const domain = mode === MODE_DEFAULT ? `${value}${suffix}` : value;
    return `https://${normalizeDomain(domain)}/login`;
};

const DomainStatusBadge = ({ status }) => {
    const { variant, text } = status;
    const { color, bg } = DOMAIN_STATUS_VARIANTS[variant];
    return (
        <span className="domain-status__badge" style={{ color, background: bg }}>
            <span className="domain-status__badge-dot" style={{ background: color }} />
            {text}
        </span>
    );
};

const DomainStatusRow = ({ label, status }) => (
    <div className="domain-status__row">
        <span className="domain-status__label">{label}</span>
        <DomainStatusBadge status={status} />
    </div>
);

const DnsInstructions = ({ dns, origin, originPlaceholder = 'seu-domínio.com.br' }) => (
    <div className="dns-instructions">
        <p className="dns-instructions__title">Configuração de DNS necessária</p>
        <div className="dns-instructions__table">
            <div className="dns-instructions__row">
                <span className="dns-instructions__key">Tipo</span>
                <code className="dns-instructions__value">{dns.type}</code>
            </div>
            <div className="dns-instructions__row">
                <span className="dns-instructions__key">Origem</span>
                <code className={`dns-instructions__value${!origin ? ' dns-instructions__value--placeholder' : ''}`}>
                    {origin || originPlaceholder}
                </code>
            </div>
            <div className="dns-instructions__row">
                <span className="dns-instructions__key">{dns.label}</span>
                <code className="dns-instructions__value">{dns.target}</code>
            </div>
        </div>
        <a
            href={MANUAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="dns-instructions__link"
        >
            Ver manual de configuração →
        </a>
    </div>
);

const LegacySystemDomainNotice = ({ domain }) => (
    <div className="domain-card__legacy">
        <div className="domain-card__legacy-current">
            <span>Domínio atual</span>
            <strong>{domain}</strong>
        </div>
        <p>
            Este domínio foi configurado em um padrão antigo. Para evitar indisponibilidade no acesso ao sistema, a alteração precisa ser realizada pela nossa central de atendimento.
        </p>
        <a
            href={WHATSAPP_DOMAIN_CHANGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="domain-card__legacy-whatsapp"
        >
            Falar com atendimento pelo WhatsApp
        </a>
    </div>
);

const DomainStatus = ({ sslBadge, dnsBadge, checkingStatus, onCheckStatus }) => (
    <div className="domain-status">
        <div className="domain-status__header">
            <span className="domain-status__title">Status</span>
            <button
                className={`domain-status__check-btn${checkingStatus ? ' domain-status__check-btn--spinning' : ''}`}
                onClick={onCheckStatus}
                disabled={checkingStatus}
                title="Checar novamente"
            >
                <Icon name="refresh" size={15} />
                Checar novamente
            </button>
        </div>
        <DomainStatusRow label="Apontamento DNS" status={dnsBadge} />
        <DomainStatusRow label="SSL"             status={sslBadge} />
    </div>
);

const DomainChangeConfirmModal = ({ confirmData, saving, redirecting, redirectCountdown, onClose, onConfirm }) => (
    <Modal
        title="Confirmar alteração do domínio"
        show={confirmData !== null}
        close={onClose}
        size="regular"
        footer={(
            <>
                <Button
                    type="secondary"
                    size="small"
                    text="Cancelar"
                    action={onClose}
                    disabled={saving || redirecting}
                />
                <Button
                    type="primary"
                    size="small"
                    text={redirecting ? `Redirecionando em ${redirectCountdown}s` : 'Confirmar alteração'}
                    action={onConfirm}
                    loading={saving}
                    disabled={redirecting}
                />
            </>
        )}
    >
        <div className="domain-card__confirm-modal">
            <div className="domain-card__confirm-highlight">
                <span>Novo endereço do sistema</span>
                <strong>{confirmData?.displayDomain}</strong>
            </div>

            <p>
                Ao confirmar, este domínio passa a ser o endereço oficial de acesso ao sistema imediatamente.
            </p>

            <div className="domain-card__confirm-steps">
                <div className="domain-card__confirm-step">
                    <span>1</span>
                    <p>Sua sessão atual será encerrada por segurança.</p>
                </div>
                <div className="domain-card__confirm-step">
                    <span>2</span>
                    <p>Você será redirecionado para a nova tela de login em {REDIRECT_DELAY_SECONDS} segundos.</p>
                </div>
            </div>
        </div>
    </Modal>
);

const DomainCard = ({ type, title, suffix, ownDomainPrefix, modeSelectionEnabled = true, dns, hint, initialMode, initialValue, sslStatus, dnsStatus, checkingStatus, onSave, onCheckStatus }) => {
    const [mode, setMode]                 = useState(initialMode ?? MODE_DEFAULT);
    const [prefix, setPrefix]             = useState('');
    const [customDomain, setCustomDomain] = useState('');
    const [saving, setSaving]             = useState(false);
    const [confirmData, setConfirmData]   = useState(null);
    const [redirecting, setRedirecting]   = useState(false);
    const [redirectCountdown, setRedirectCountdown] = useState(REDIRECT_DELAY_SECONDS);
    const redirectTimerRef                = useRef(null);
    const redirectIntervalRef             = useRef(null);
    const currentDomain                   = normalizeDomain(initialValue);
    const isLegacySystemDomain            = type === 'system' && hasDomainFormat(initialValue) && !isDomainEndingWithSuffix(initialValue, suffix);

    useEffect(() => () => {
        if (redirectTimerRef.current) {
            clearTimeout(redirectTimerRef.current);
        }
        if (redirectIntervalRef.current) {
            clearInterval(redirectIntervalRef.current);
        }
    }, []);

    useEffect(() => {
        if (!initialMode) return;
        if (!modeSelectionEnabled) {
            setMode(MODE_DEFAULT);
            setPrefix(getPrefixFromSubdomain(initialValue, suffix));
            setCustomDomain('');
            return;
        }

        setMode(initialMode);
        if (initialMode === MODE_DEFAULT) {
            setPrefix(getPrefixFromSubdomain(initialValue, suffix));
            setCustomDomain('');
        } else {
            setCustomDomain(stripDomainPrefix(initialValue, ownDomainPrefix));
            setPrefix('');
        }
    }, [initialMode, initialValue, modeSelectionEnabled, ownDomainPrefix, suffix]);

    const handleCustomDomainChange = value => {
        setCustomDomain(stripDomainPrefix(value, ownDomainPrefix));
    };

    const isSystemDomainChange = (nextMode, nextValue) => {
        if (type !== 'system') return false;
        if (!modeSelectionEnabled && initialMode !== MODE_DEFAULT) return true;
        return initialMode !== nextMode || getPrefixFromSubdomain(initialValue, suffix) !== nextValue;
    };

    const performSave = async ({ nextMode, value, redirectUrl, redirectAfterSave = false }) => {
        setSaving(true);
        try {
            await onSave(type, nextMode, value);
            toast.success('Configuração salva com sucesso.');

            if (redirectAfterSave) {
                setRedirecting(true);
                setRedirectCountdown(REDIRECT_DELAY_SECONDS);
                logout();
                redirectIntervalRef.current = setInterval(() => {
                    setRedirectCountdown(current => Math.max(current - 1, 0));
                }, 1000);
                redirectTimerRef.current = setTimeout(() => {
                    window.location.href = redirectUrl;
                }, REDIRECT_DELAY_SECONDS * 1000);
            }
        } catch (err) {
            renderError(err);
            setConfirmData(null);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        const rawValue = mode === MODE_DEFAULT ? prefix : customDomain;
        if (!rawValue.trim()) {
            toast.warning('Preencha o domínio antes de salvar.');
            return;
        }
        const value = mode === MODE_DEFAULT ? prefix.trim() : buildDomainWithPrefix(customDomain.trim(), ownDomainPrefix);

        if (isSystemDomainChange(mode, value)) {
            setConfirmData({
                nextMode: mode,
                value,
                displayDomain: normalizeDomain(mode === MODE_DEFAULT ? `${value}${suffix}` : value),
                redirectUrl: buildRedirectUrl(mode, value, suffix),
            });
            return;
        }

        performSave({ nextMode: mode, value });
    };

    const handleConfirmSystemChange = () => {
        if (!confirmData) return;
        performSave({ ...confirmData, redirectAfterSave: true });
    };

    const handleCloseConfirmModal = () => {
        if (saving || redirecting) return;
        setConfirmData(null);
    };

    const sslBadge = checkingStatus ? CHECKING_STATUS : (SSL_STATUS[sslStatus] ?? SSL_STATUS.disconnected);
    const dnsBadge = checkingStatus ? CHECKING_STATUS : (DNS_STATUS[dnsStatus] ?? DNS_STATUS.disconnected);

    return (
        <Box title={title} className={isLegacySystemDomain ? LEGACY_SYSTEM_CARD_CLASS : ''}>
            {modeSelectionEnabled && (
                <InputRadio
                    options={DOMAIN_MODE_OPTIONS}
                    value={mode}
                    change={setMode}
                />
            )}
            {!modeSelectionEnabled && (
                <div className="domain-card__mode-placeholder">
                    {isLegacySystemDomain ? 'Domínio configurado fora do padrão atual' : 'Personalize o domínio do seu sistema'}
                </div>
            )}

            {isLegacySystemDomain ? (
                <LegacySystemDomainNotice domain={currentDomain} />
            ) : (
                <>
                    <div className="domain-card__input-row">
                        {mode === MODE_DEFAULT ? (
                            <Input
                                label="Prefixo do subdomínio"
                                placeholder="ex: minha-loja"
                                value={prefix}
                                change={setPrefix}
                                mask="convertToSubdomain"
                                right={<span className="domain-card__suffix">{suffix}</span>}
                            />
                        ) : (
                            <>
                                <Input
                                    label="Domínio próprio"
                                    placeholder={ownDomainPrefix ? 'minhaempresa.com.br' : 'Ex: minhaempresa.com.br'}
                                    value={customDomain}
                                    change={handleCustomDomainChange}
                                    mask="convertToDomain"
                                    left={ownDomainPrefix ? <span className="domain-card__prefix">{ownDomainPrefix}</span> : null}
                                />
                                {hint && <p className="domain-card__hint">{hint}</p>}
                            </>
                        )}
                    </div>

                    {mode === MODE_OWN && (
                        <DnsInstructions
                            dns={dns}
                            origin={customDomain ? buildDomainWithPrefix(customDomain, ownDomainPrefix) : ''}
                            originPlaceholder={dns.originPlaceholder}
                        />
                    )}

                    <DomainStatus
                        sslBadge={sslBadge}
                        dnsBadge={dnsBadge}
                        checkingStatus={checkingStatus}
                        onCheckStatus={onCheckStatus}
                    />

                    <div className="domain-card__save-row">
                        <Button
                            type="primary"
                            size="small"
                            svg="save-white"
                            text="Salvar"
                            action={handleSave}
                            loading={saving}
                        />
                    </div>
                </>
            )}

            <DomainChangeConfirmModal
                confirmData={confirmData}
                saving={saving}
                redirecting={redirecting}
                redirectCountdown={redirectCountdown}
                onClose={handleCloseConfirmModal}
                onConfirm={handleConfirmSystemChange}
            />
        </Box>
    );
};

const DomainSettings = () => {
    const [loading, setLoading]               = useState(true);
    const [siteData, setSiteData]             = useState(null);
    const [systemData, setSystemData]         = useState(null);
    const [siteStatus, setSiteStatus]         = useState({ ssl: null, dns: null });
    const [systemStatus, setSystemStatus]     = useState({ ssl: null, dns: null });
    const [checkingSite, setCheckingSite]     = useState(false);
    const [checkingSystem, setCheckingSystem] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get('/domain-settings');
                const { site, system } = res.data.resource;
                setSiteData(site);
                setSystemData(system);
            } catch (err) {
                renderError(err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const checkStatus = useCallback(async (type) => {
        const setChecking = type === 'site' ? setCheckingSite : setCheckingSystem;
        const setStatus   = type === 'site' ? setSiteStatus   : setSystemStatus;
        setChecking(true);
        try {
            const res = await api.post('/domain-settings/check-status', { type });
            setStatus({ ssl: res.data.ssl_status, dns: res.data.dns_status });
        } catch (err) {
            renderError(err);
        } finally {
            setChecking(false);
        }
    }, []);

    // Checa status automaticamente ao carregar os dados
    useEffect(() => {
        if (!loading) {
            checkStatus('site');
            checkStatus('system');
        }
    }, [loading, checkStatus]);

    const handleSave = async (type, mode, value) => {
        await api.put('/domain-settings', { type, mode, value });
    };

    if (loading) {
        return (
            <PanelTemplate id="domain-settings">
                <Loading show={true} />
            </PanelTemplate>
        );
    }

    return (
        <PanelTemplate id="domain-settings">
            <div className="row">
                <div className="col-6">
                    <DomainCard
                        type="site"
                        title="Site"
                        suffix={SITE_SUFFIX}
                        dns={SITE_DNS}
                        initialMode={siteData?.mode}
                        initialValue={siteData?.value}
                        sslStatus={siteStatus.ssl}
                        dnsStatus={siteStatus.dns}
                        checkingStatus={checkingSite}
                        onSave={handleSave}
                        onCheckStatus={() => checkStatus('site')}
                    />
                </div>
                <div className="col-6">
                    <DomainCard
                        type="system"
                        title="Sistema"
                        suffix={SYSTEM_SUFFIX}
                        ownDomainPrefix="sistema."
                        modeSelectionEnabled={false}
                        dns={SYSTEM_DNS}
                        initialMode={systemData?.mode}
                        initialValue={systemData?.value}
                        sslStatus={systemStatus.ssl}
                        dnsStatus={systemStatus.dns}
                        checkingStatus={checkingSystem}
                        onSave={handleSave}
                        onCheckStatus={() => checkStatus('system')}
                    />
                </div>
            </div>
        </PanelTemplate>
    );
};

export default DomainSettings;
